import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../helpers/AxiosInstance";
import styles from "../../assets/css/modules/notification.module.css";

function dismissNotification(Serial) {
  const instance = axiosInstance();
  instance.post(`DismissNotification/${Serial}`);
}

function getStatusStyles(statusName) {
  const name = statusName?.toLowerCase() || "";
  if (name.includes("وصل") || name.includes("تسليم") || name.includes("delivered")) {
    return { bg: "rgba(34,197,94,0.12)", color: "#16a34a", border: "rgba(34,197,94,0.25)" };
  }
  if (name.includes("جاري") || name.includes("خارج") || name.includes("delivery") || name.includes("progress")) {
    return { bg: "rgba(59,130,246,0.12)", color: "#2563eb", border: "rgba(59,130,246,0.25)" };
  }
  if (name.includes("لم يتم") || name.includes("تأجيل") || name.includes("محاولة") || name.includes("delay") || name.includes("fail")) {
    return { bg: "rgba(245,158,11,0.12)", color: "#d97706", border: "rgba(245,158,11,0.25)" };
  }
  if (name.includes("يرد") || name.includes("إلغاء") || name.includes("رفض") || name.includes("cancel") || name.includes("return")) {
    return { bg: "rgba(239,68,68,0.12)", color: "#dc2626", border: "rgba(239,68,68,0.25)" };
  }
  return { bg: "rgba(100,116,139,0.12)", color: "#64748b", border: "rgba(100,116,139,0.25)" };
}

export default function Notification({
  notificationIndex, notifications, setNotifications,
  primary, Status, AWB, Body, Serial,
  ConsigneeName, ConsigneePhone, RunnerName, RunnerPhone, ReasonName, COD,
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("notifications");
  const isAr = i18n.language?.startsWith("ar");
  const [hovered, setHovered] = useState(false);
  const st = getStatusStyles(Status);

  const rows = [];
  if (ConsigneeName) rows.push({ label: isAr ? "العميل" : "Client", value: ConsigneeName });
  if (ConsigneePhone) rows.push({ label: isAr ? "الهاتف" : "Phone", value: ConsigneePhone });
  if (RunnerName) rows.push({ label: isAr ? "المندوب" : "Runner", value: RunnerPhone ? `${RunnerName} (${RunnerPhone})` : RunnerName });
  if (COD > 0) rows.push({ label: isAr ? "مبلغ التحصيل" : "COD Amount", value: `${COD} EGP` });

  if (primary) {
    return (
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => { dismissNotification(Serial); navigate(`/shipment/${AWB}`); }}
        style={{
          background: "#fff",
          border: `1px solid ${hovered ? "var(--primary-color,#435f86)" : "#e2e8f0"}`,
          borderRadius: 14,
          padding: "20px 22px 20px 26px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          position: "relative",
          boxShadow: hovered ? "0 8px 24px -6px rgba(67,95,134,0.15)" : "0 2px 8px rgba(0,0,0,0.04)",
          transform: hovered ? "translateY(-2px)" : "none",
          transition: "all 0.22s ease",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        {/* accent bar */}
        <div style={{ position:"absolute", top:1, bottom:1, left:1, width:4, borderRadius:"12px 0 0 12px", background: hovered ? "var(--primary-color,#435f86)" : "#cbd5e1", transition:"background 0.22s ease" }} />

        {/* header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:12.5, fontWeight:600, color:"#94a3b8" }}>{t("shipment_number")}:</span>
            <span style={{ background:"rgba(67,95,134,0.07)", color:"var(--primary-color,#435f86)", border:"1px solid rgba(67,95,134,0.14)", borderRadius:8, padding:"5px 13px", fontSize:14, fontWeight:700, letterSpacing:0.4 }}>{AWB}</span>
          </div>
          <span style={{ background:st.bg, color:st.color, border:`1px solid ${st.border}`, borderRadius:9999, padding:"5px 14px", fontSize:13, fontWeight:700 }}>{Status}</span>
        </div>

        {/* separator */}
        <div style={{ borderTop:"1px dashed #e2e8f0", margin:"0" }} />

        {/* data grid */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:"14px 20px" }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display:"flex", flexDirection:"column", gap:3 }}>
              <span style={{ fontSize:12, fontWeight:600, color:"#94a3b8" }}>{r.label}</span>
              <span style={{ fontSize:14, fontWeight:700, color:"#1e293b", wordBreak:"break-word" }}>{r.value}</span>
            </div>
          ))}
        </div>

        {/* reason */}
        {ReasonName ? (
          <div style={{ background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.18)", borderRadius:8, padding:"10px 14px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <span style={{ fontWeight:700, color:"#dc2626", fontSize:13 }}>{isAr ? "السبب:" : "Reason:"}</span>
            <span style={{ fontWeight:800, color:"#b91c1c", fontSize:13.5 }}>{ReasonName}</span>
          </div>
        ) : null}
      </div>
    );
  }

  /* compact (navbar popover) */
  const firstLine = (Body || "").split("\n").map(s => s.trim()).filter(Boolean)[0] || "";
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => {
        navigate(`/shipment/${AWB}`);
        dismissNotification(Serial);
        if (setNotifications && notifications) setNotifications(notifications.filter((_,i) => i !== notificationIndex));
      }}
      style={{
        background:"#fff",
        border:`1px solid ${hovered ? "var(--primary-color,#435f86)" : "#e2e8f0"}`,
        borderRadius:10,
        padding:"11px 14px 11px 18px",
        cursor:"pointer",
        display:"flex",
        flexDirection:"column",
        gap:6,
        position:"relative",
        boxShadow: hovered ? "0 4px 12px rgba(67,95,134,0.1)" : "0 1px 3px rgba(0,0,0,0.03)",
        transform: hovered ? "translateY(-1px)" : "none",
        transition:"all 0.2s ease",
        boxSizing:"border-box",
        width:"100%",
      }}
    >
      <div style={{ position:"absolute", top:1, bottom:1, left:1, width:3, background: hovered ? "var(--primary-color,#435f86)" : "#cbd5e1", borderRadius:"8px 0 0 8px", transition:"background 0.2s ease" }} />
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:11.5, fontWeight:600, color:"#94a3b8" }}>{t("shipment_number")}:</span>
          <span style={{ background:"rgba(67,95,134,0.07)", color:"var(--primary-color,#435f86)", border:"1px solid rgba(67,95,134,0.14)", borderRadius:6, padding:"3px 10px", fontSize:13, fontWeight:700 }}>{AWB}</span>
        </div>
        <span style={{ background:st.bg, color:st.color, borderRadius:9999, padding:"3px 10px", fontSize:11.5, fontWeight:700, whiteSpace:"nowrap" }}>{Status}</span>
      </div>
      {firstLine ? <span style={{ fontSize:12.5, color:"#64748b", fontWeight:500, paddingRight:4 }}>{firstLine}</span> : null}
    </div>
  );
}
